import React, { createContext, useState, useEffect, useRef, useCallback } from "react";
import { ENDPOINTS } from "../config/api";

export const AuthContext = createContext();
export const useAuth = () => React.useContext(AuthContext);
// Tiempo de inactividad (5 minutos)
const INACTIVITY_TIME = 5 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  // const [token, setToken] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("token"));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const inactivityTimer = useRef(null);

  // ---------------- CERRAR SESIÓN ----------------
  const logout = useCallback(() => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    window.location.href = "/"; // redirige al login
  }, []);

  // ---------------- LOGOUT AUTOMÁTICO POR INACTIVIDAD ----------------
  const autoLogout = useCallback(() => {
    console.log("Sesión cerrada por inactividad");
    logout();
  }, [logout]);

  // ---------------- REINICIAR TIMER DE INACTIVIDAD ----------------
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    if (token) {
      inactivityTimer.current = setTimeout(() => {
        autoLogout();
      }, INACTIVITY_TIME);
    }
  }, [token, autoLogout]);

  // ---------------- DETECTAR ACTIVIDAD ----------------
  useEffect(() => {
    const activityEvents = ["mousemove", "keydown", "click", "scroll"];
    activityEvents.forEach((event) =>
      window.addEventListener(event, resetInactivityTimer)
    );

    return () => {
      activityEvents.forEach((event) =>
        window.removeEventListener(event, resetInactivityTimer)
      );
    };
  }, [resetInactivityTimer]);

  // ---------------- INICIAR TIMER AL INICIAR SESIÓN ----------------
  useEffect(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  // ---------------- CARGAR TOKEN DESDE LOCALSTORAGE ----------------
  useEffect(() => {
    const savedToken = localStorage.getItem("token");
    if (savedToken) {
      setToken(savedToken);
      fetchUser(savedToken);
    } else {
      setLoading(false);
    }
  }, []);

  // ---------------- OBTENER INFO DEL USUARIO DESDE BACKEND ----------------
  const fetchUser = async (tokenParam) => {
    try {
      const res = await fetch(`${ENDPOINTS.me}`, {
        headers: { Authorization: `Bearer ${tokenParam}` },
      });
      const data = await res.json();
      if (data.success) setUser(data.user); // user incluye rol
    } catch (err) {
      console.error("Error al traer usuario:", err);
    } finally {
      setLoading(false);
    }
  };

  // ---------------- LOGIN ----------------
  const login = (data) => {
    localStorage.setItem("token", data.token);
    setToken(data.token);

    // Guardar el usuario si viene en la respuesta (IMPORTANTE)
    if (data.user) {
      setUser(data.user);
    } else {
      fetchUser(data.token);
    }

    resetInactivityTimer();
  };

  // ---------------- FETCH SEGURA ----------------
const authFetch = async (url, options = {}) => {
  if (!token) return { success: false, message: "Token no disponible" };

  const isFormData = options.body instanceof FormData;

  const headers = {
    Authorization: `Bearer ${token}`,
    ...options.headers,
  };

  // Solo agregar JSON si NO es FormData
  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  try {
    const res = await fetch(url, { ...options, headers });
    const data = await res.json();

    // Token expirado → cerrar sesión
    if (!data.success && data.message === "Token inválido o expirado") {
      logout();
      return { success: false, message: "Sesión cerrada por token inválido" };
    }

    // Sin permisos → NO cerrar sesión
    if (!data.success && data.message === "Acceso denegado") {
      return { success: false, message: "No tienes permisos para este recurso" };
    }

    return data;

  } catch (err) {
    console.error("Error en authFetch:", err);
    return { success: false, message: "Error de red" };
  }
};
  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        setUser,
        login,
        logout,
        loading,
        authFetch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};